import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type FC,
  type CSSProperties,
  type ChangeEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type SyntheticEvent,
} from 'react';
import {
  X,
  Download,
  ChevronLeft,
  ChevronRight,
  FileText,
  Image,
  File,
  Loader2,
  ZoomIn,
  ZoomOut,
  Maximize,
  MoveHorizontal,
  MoveVertical,
} from 'lucide-react';
import PdfViewer, { FIT_MODES, type FitMode, type PdfViewerHandle } from '@/components/ui/PdfViewer';
import { supabase } from '@/lib/supabase';

// ---------- Companion types (file-local per B.12b coupled-pair precedent) ----------

interface FileTypeBadge {
  label: string;
  color: string;
  bg: string;
}

// Minimal file descriptor consumed by this modal. Consumers (GenericFile,
// ObraArquivoDrawer, ObraArquivos) pass these shapes — all fields optional
// because consumers route through Raw-alias boundary casts today.
export interface PreviewFile {
  id?: string;
  name?: string;
  /** Rótulo exibido no header (fallback para `name`). Permite mostrar a
   *  descrição em vez do nome de storage/UUID — ex.: catálogo de proposta. */
  displayName?: string;
  mimeType?: string | null;
  bucketName?: string;
  storagePath?: string;
  size?: number | null;
}

interface PdfStateSnapshot {
  zoom: number;
  pageNumber: number;
  numPages: number | null;
  fitMode: FitMode | null;
}

interface PdfBlobContent {
  blob: Blob;
}
interface PdfSignedUrlContent {
  signedUrl: string;
}
interface FallbackContent {
  fallback: true;
}
type PreviewContent = PdfBlobContent | PdfSignedUrlContent | FallbackContent;

// All-optional superset. Consumers supply whatever subset they need; any extra
// keys they pass flow through because of the boundary cast (Record<string,
// unknown>) on the consumer side.
export interface FilePreviewModalProps {
  isOpen?: boolean;
  files?: PreviewFile[];
  currentIndex?: number;
  onClose?: () => void;
  onNavigate?: (index: number) => void;
  onDownload?: (file: PreviewFile) => void;
}

interface PdfControlsProps {
  pdfViewerRef: React.RefObject<PdfViewerHandle | null>;
  pdfState: PdfStateSnapshot;
}

const FILE_TYPE_BADGES: Record<string, FileTypeBadge> = {
  'application/pdf': { label: 'PDF', color: '#dc2626', bg: '#fecaca' },
  'image/jpeg': { label: 'JPG', color: '#7c3aed', bg: '#ddd6fe' },
  'image/png': { label: 'PNG', color: '#7c3aed', bg: '#ddd6fe' },
  'image/gif': { label: 'GIF', color: '#7c3aed', bg: '#ddd6fe' },
  'application/dwg': { label: 'DWG', color: '#d97706', bg: '#fef3c7' },
  'application/x-dwg': { label: 'DWG', color: '#d97706', bg: '#fef3c7' },
  'image/vnd.dwg': { label: 'DWG', color: '#d97706', bg: '#fef3c7' },
  'application/msword': { label: 'DOC', color: '#2563eb', bg: '#dbeafe' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
    label: 'DOCX',
    color: '#2563eb',
    bg: '#dbeafe',
  },
  'application/vnd.ms-excel': { label: 'XLS', color: '#15803d', bg: '#dcfce7' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {
    label: 'XLSX',
    color: '#15803d',
    bg: '#dcfce7',
  },
  'text/csv': { label: 'CSV', color: '#15803d', bg: '#dcfce7' },
  'application/xml': { label: 'XML', color: '#6b7280', bg: '#e5e7eb' },
  'text/xml': { label: 'XML', color: '#6b7280', bg: '#e5e7eb' },
};

function getBadge(mimeType: string | null | undefined, fileName: string | undefined): FileTypeBadge {
  if (mimeType && FILE_TYPE_BADGES[mimeType]) return FILE_TYPE_BADGES[mimeType];
  const ext = fileName?.split('.').pop()?.toUpperCase() || 'FILE';
  return { label: ext, color: '#6b7280', bg: '#e5e7eb' };
}

function formatFileSize(bytes: number | null | undefined): string | null {
  if (!bytes) return null;
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function getFileIcon(mimeType: string | null | undefined) {
  if (mimeType?.startsWith('image/')) return Image;
  if (mimeType === 'application/pdf') return FileText;
  return File;
}

const ZOOM_PRESETS = [25, 50, 75, 100, 125, 150, 200, 300] as const;

const pdfCtrlBtn = (disabled: boolean): CSSProperties => ({
  background: 'none',
  border: '1px solid rgba(255,255,255,0.3)',
  borderRadius: '4px',
  width: '26px',
  height: '26px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: disabled ? 0.4 : 1,
});

const pdfFitBtn = (active: boolean): CSSProperties => ({
  background: active ? 'rgba(255,255,255,0.2)' : 'none',
  border: active ? '1px solid rgba(255,255,255,0.5)' : '1px solid rgba(255,255,255,0.3)',
  borderRadius: '4px',
  width: '26px',
  height: '26px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

function PdfControls({ pdfViewerRef, pdfState }: PdfControlsProps) {
  const v = pdfViewerRef.current;
  if (!v || !pdfState) return null;
  const { zoom, pageNumber, numPages, fitMode } = pdfState;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
      {/* Page navigation */}
      <button
        onClick={() => v.goToPage(pageNumber - 1)}
        disabled={pageNumber <= 1}
        style={pdfCtrlBtn(pageNumber <= 1)}
      >
        <ChevronLeft size={13} color="white" />
      </button>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '3px',
          fontSize: '12px',
          color: 'white',
        }}
      >
        <input
          type="text"
          value={pageNumber}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const val = parseInt(e.target.value, 10);
            if (!isNaN(val)) v.goToPage(val);
          }}
          onKeyDown={(e: ReactKeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
          style={{
            width: '36px',
            textAlign: 'center',
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: '4px',
            padding: '2px',
            fontSize: '12px',
            color: 'white',
            background: 'rgba(255,255,255,0.1)',
            outline: 'none',
          }}
        />
        <span>de {numPages || '...'}</span>
      </div>
      <button
        onClick={() => v.goToPage(pageNumber + 1)}
        disabled={!numPages || pageNumber >= numPages}
        style={pdfCtrlBtn(!numPages || pageNumber >= numPages)}
      >
        <ChevronRight size={13} color="white" />
      </button>

      <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.2)' }} />

      {/* Fit modes */}
      <button
        onClick={() => v.setFitMode(FIT_MODES.WIDTH)}
        title="Caber na largura"
        style={pdfFitBtn(fitMode === FIT_MODES.WIDTH)}
      >
        <MoveHorizontal size={13} color="white" />
      </button>
      <button
        onClick={() => v.setFitMode(FIT_MODES.HEIGHT)}
        title="Caber na altura"
        style={pdfFitBtn(fitMode === FIT_MODES.HEIGHT)}
      >
        <MoveVertical size={13} color="white" />
      </button>
      <button
        onClick={() => v.setFitMode(FIT_MODES.PAGE)}
        title="Caber inteiro"
        style={pdfFitBtn(fitMode === FIT_MODES.PAGE)}
      >
        <Maximize size={13} color="white" />
      </button>

      <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.2)' }} />

      {/* Zoom */}
      <button
        onClick={() => v.setZoom((z: number) => z - 0.25)}
        disabled={zoom <= 0.25}
        style={pdfCtrlBtn(zoom <= 0.25)}
      >
        <ZoomOut size={13} color="white" />
      </button>
      <select
        value=""
        onChange={(e: ChangeEvent<HTMLSelectElement>) => {
          const val = parseFloat(e.target.value);
          if (!isNaN(val)) v.setZoom(() => val);
        }}
        style={{
          appearance: 'none',
          background: 'rgba(255,255,255,0.1)',
          border: '1px solid rgba(255,255,255,0.3)',
          borderRadius: '4px',
          fontSize: '11px',
          color: 'white',
          fontWeight: 500,
          padding: '3px 6px',
          minWidth: '52px',
          textAlign: 'center',
          cursor: 'pointer',
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 3px center',
          paddingRight: '16px',
        }}
      >
        <option value="" disabled hidden>
          {Math.round(zoom * 100)}%
        </option>
        {ZOOM_PRESETS.map((pct) => (
          <option key={pct} value={pct / 100}>
            {pct}%
          </option>
        ))}
      </select>
      <button
        onClick={() => v.setZoom((z: number) => z + 0.25)}
        disabled={zoom >= 3}
        style={pdfCtrlBtn(zoom >= 3)}
      >
        <ZoomIn size={13} color="white" />
      </button>
    </div>
  );
}

/**
 * Unified file preview modal with navigation between files.
 */
const FilePreviewModal: FC<FilePreviewModalProps> = ({
  isOpen,
  files = [],
  currentIndex = 0,
  onClose,
  onNavigate,
  onDownload,
}) => {
  const [content, setContent] = useState<PreviewContent | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [imgZoom, setImgZoom] = useState<number>(1);
  const [imgNaturalSize, setImgNaturalSize] = useState<{ width: number; height: number } | null>(
    null,
  );
  const contentCache = useRef<Map<string, PreviewContent>>(new Map<string, PreviewContent>());
  const pdfViewerRef = useRef<PdfViewerHandle | null>(null);
  const imgContainerRef = useRef<HTMLDivElement | null>(null);
  const [pdfState, setPdfState] = useState<PdfStateSnapshot | null>(null);

  const file: PreviewFile | undefined = files[currentIndex];
  const total = files.length;
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < total - 1;

  const isImage = Boolean(file?.mimeType?.startsWith('image/'));
  const isPdf = file?.mimeType === 'application/pdf';

  // Load content for a file
  const loadContent = useCallback(async (f: PreviewFile | undefined): Promise<PreviewContent | null> => {
    if (!f) return null;
    const cacheKey = f.id;
    if (cacheKey && contentCache.current.has(cacheKey)) {
      return contentCache.current.get(cacheKey) ?? null;
    }

    const mime = f.mimeType;
    const bucket = f.bucketName ?? '';
    const path = f.storagePath ?? '';

    if (mime === 'application/pdf') {
      const { data, error } = await supabase.storage.from(bucket).download(path);
      if (error) throw error;
      const result: PreviewContent = { blob: new Blob([data], { type: 'application/pdf' }) };
      if (cacheKey) contentCache.current.set(cacheKey, result);
      return result;
    }

    if (mime?.startsWith('image/')) {
      const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 300);
      if (error) throw error;
      const result: PreviewContent = { signedUrl: data.signedUrl };
      if (cacheKey) contentCache.current.set(cacheKey, result);
      return result;
    }

    const result: PreviewContent = { fallback: true };
    if (cacheKey) contentCache.current.set(cacheKey, result);
    return result;
  }, []);

  // Load current file content + preload adjacent
  useEffect(() => {
    if (!isOpen || !file) return;
    let cancelled = false;

    setImgZoom(1);
    setImgNaturalSize(null);
    const load = async () => {
      setLoading(true);
      try {
        const result = await loadContent(file);
        if (!cancelled) setContent(result);
      } catch (err) {
        console.error('Erro ao carregar preview:', err);
        if (!cancelled) setContent({ fallback: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    // Preload adjacent
    if (files[currentIndex - 1]) loadContent(files[currentIndex - 1]).catch(() => {});
    if (files[currentIndex + 1]) loadContent(files[currentIndex + 1]).catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [isOpen, currentIndex, file, files, loadContent]);

  // Sync PDF viewer state to render controls in header
  const syncPdfState = useCallback((state: PdfStateSnapshot | null) => {
    if (state) {
      setPdfState(state);
    } else {
      const v = pdfViewerRef.current;
      if (v) {
        setPdfState({
          zoom: v.zoom,
          pageNumber: v.pageNumber,
          numPages: v.numPages,
          fitMode: v.fitMode,
        });
      }
    }
  }, []);

  // Clear cache on close
  useEffect(() => {
    if (!isOpen) {
      contentCache.current.clear();
      setPdfState(null);
      setContent(null);
    }
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'ArrowLeft' && hasPrev) onNavigate?.(currentIndex - 1);
      if (e.key === 'ArrowRight' && hasNext) onNavigate?.(currentIndex + 1);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, hasPrev, hasNext, onClose, onNavigate]);

  if (!isOpen || !file) return null;

  const badge = getBadge(file.mimeType, file.name);
  const IconComponent = getFileIcon(file.mimeType);

  const navBtnStyle = (disabled: boolean): CSSProperties => ({
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    background: 'rgba(0,0,0,0.6)',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0 : 0.8,
    pointerEvents: disabled ? 'none' : 'auto',
    transition: 'opacity 0.2s',
    zIndex: 10,
  });

  // Content narrowing helpers — union discriminators for the JSX branch below.
  const hasSignedUrl = (c: PreviewContent | null): c is PdfSignedUrlContent =>
    !!c && 'signedUrl' in c && typeof c.signedUrl === 'string';
  const hasBlob = (c: PreviewContent | null): c is PdfBlobContent =>
    !!c && 'blob' in c && c.blob instanceof Blob;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 10010,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 20px',
          background: 'rgba(0,0,0,0.4)',
          flexShrink: 0,
        }}
      >
        {/* Left: counter + name + badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
          {total > 1 && (
            <span style={{ color: '#9ca3af', fontSize: '13px', flexShrink: 0 }}>
              {currentIndex + 1} de {total}
            </span>
          )}
          <span
            style={{
              color: 'white',
              fontSize: '14px',
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {file.displayName ?? file.name}
          </span>
          <span
            style={{
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: 600,
              textTransform: 'uppercase',
              flexShrink: 0,
              background: badge.bg,
              color: badge.color,
            }}
          >
            {badge.label}
          </span>
          {file.size && (
            <span style={{ color: '#9ca3af', fontSize: '12px', flexShrink: 0 }}>
              {formatFileSize(file.size)}
            </span>
          )}
        </div>

        {/* Center: PDF controls (when viewing PDF) */}
        {isPdf && pdfState && <PdfControls pdfViewerRef={pdfViewerRef} pdfState={pdfState} />}

        {/* Center: Image zoom controls */}
        {isImage && !loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
            <button
              onClick={() => setImgZoom((z) => Math.max(0.25, Math.round((z - 0.25) * 100) / 100))}
              disabled={imgZoom <= 0.25}
              style={pdfCtrlBtn(imgZoom <= 0.25)}
            >
              <ZoomOut size={13} color="white" />
            </button>
            <select
              value=""
              onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                const val = parseFloat(e.target.value);
                if (!isNaN(val)) setImgZoom(val);
              }}
              style={{
                appearance: 'none',
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '4px',
                fontSize: '11px',
                color: 'white',
                fontWeight: 500,
                padding: '3px 6px',
                minWidth: '52px',
                textAlign: 'center',
                cursor: 'pointer',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 3px center',
                paddingRight: '16px',
              }}
            >
              <option value="" disabled hidden>
                {Math.round(imgZoom * 100)}%
              </option>
              {ZOOM_PRESETS.map((pct) => (
                <option key={pct} value={pct / 100}>
                  {pct}%
                </option>
              ))}
            </select>
            <button
              onClick={() => setImgZoom((z) => Math.min(3, Math.round((z + 0.25) * 100) / 100))}
              disabled={imgZoom >= 3}
              style={pdfCtrlBtn(imgZoom >= 3)}
            >
              <ZoomIn size={13} color="white" />
            </button>
          </div>
        )}

        {/* Right: download + close */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <button
            onClick={() => onDownload?.(file)}
            title="Baixar"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              padding: '6px',
              borderRadius: '6px',
            }}
          >
            <Download size={18} color="white" />
          </button>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              padding: '6px',
              borderRadius: '6px',
            }}
          >
            <X size={22} color="white" />
          </button>
        </div>
      </div>

      {/* Content area with navigation arrows */}
      <div style={{ flex: 1, position: 'relative', display: 'flex', minHeight: 0 }}>
        {/* Prev arrow */}
        {total > 1 && (
          <button
            onClick={() => hasPrev && onNavigate?.(currentIndex - 1)}
            style={{ ...navBtnStyle(!hasPrev), left: '16px' }}
          >
            <ChevronLeft size={24} color="white" />
          </button>
        )}

        {/* Content */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isPdf && hasBlob(content) ? '0' : '16px 70px',
            minHeight: 0,
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'white' }}>
              <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
              Carregando...
            </div>
          ) : hasSignedUrl(content) && isImage ? (
            /* Image viewer — fit-to-page by default, zoom scales from there */
            <div
              ref={imgContainerRef}
              style={{
                overflow: 'auto',
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src={content.signedUrl}
                alt={file.name}
                onLoad={(e: SyntheticEvent<HTMLImageElement>) => {
                  const img = e.target as HTMLImageElement;
                  const nat = { width: img.naturalWidth, height: img.naturalHeight };
                  setImgNaturalSize(nat);
                  // Calculate fit-to-page zoom
                  const container = imgContainerRef.current;
                  if (container) {
                    const cw = container.clientWidth - 32;
                    const ch = container.clientHeight - 32;
                    const fitZoom = Math.min(cw / nat.width, ch / nat.height, 1);
                    setImgZoom(Math.round(fitZoom * 100) / 100);
                  }
                }}
                style={{
                  width: imgNaturalSize ? imgNaturalSize.width * imgZoom : 'auto',
                  height: imgNaturalSize ? imgNaturalSize.height * imgZoom : 'auto',
                  maxWidth: imgNaturalSize ? 'none' : '100%',
                  maxHeight: imgNaturalSize ? 'none' : '100%',
                  borderRadius: '4px',
                  transition: 'width 0.15s ease-out, height 0.15s ease-out',
                }}
              />
            </div>
          ) : hasBlob(content) && isPdf ? (
            /* PDF viewer */
            <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
              <PdfViewer
                ref={pdfViewerRef}
                file={content.blob}
                transparent
                showZoom={false}
                showPageIndicator={false}
                onStateChange={syncPdfState}
              />
            </div>
          ) : (
            /* Fallback for non-previewable files */
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
                color: 'white',
                textAlign: 'center',
              }}
            >
              <IconComponent size={64} strokeWidth={1.5} color="#9ca3af" />
              <div>
                <div style={{ fontSize: '16px', fontWeight: 500, marginBottom: '4px' }}>
                  {file.name}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    justifyContent: 'center',
                  }}
                >
                  <span
                    style={{
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      background: badge.bg,
                      color: badge.color,
                    }}
                  >
                    {badge.label}
                  </span>
                  {file.size && (
                    <span style={{ color: '#9ca3af', fontSize: '13px' }}>
                      {formatFileSize(file.size)}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ color: '#9ca3af', fontSize: '13px' }}>
                Este tipo de arquivo não pode ser visualizado no navegador
              </div>
              <button
                onClick={() => onDownload?.(file)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: '#154680',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  cursor: 'pointer',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                <Download size={16} />
                Baixar arquivo
              </button>
            </div>
          )}
        </div>

        {/* Next arrow */}
        {total > 1 && (
          <button
            onClick={() => hasNext && onNavigate?.(currentIndex + 1)}
            style={{ ...navBtnStyle(!hasNext), right: '16px' }}
          >
            <ChevronRight size={24} color="white" />
          </button>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default FilePreviewModal;
